import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { EnCamino, EnCaminoDetail } from "../../components/promos/tipos";

const base = (campusId: number) => `${process.env.REACT_APP_API_URL_V2}/admin/campus/${campusId}/promotions`;

export interface PromoApplicationFilters {
	from?: string;
	to?: string;
	statusId?: string;
	promotionId?: string;
	advisorId?: string;
	q?: string;
	page?: number;
	/** Filas por página (el servidor da hasta 200). */
	perPage?: number;
	/** Pestaña del módulo: porAplicar | aplicadas | kits | anuladas. */
	tab?: string;
}

export interface PromoRequestCreated { intentId: number; promoName: string; fits?: "exacto" | "faltan" | "sobra" | null; message?: string | null }
export interface PromoRequestRow {
	intentId: number; createdAt: string | null; requestedBy: { id: number; name: string } | null;
	student: { id: number; name: string; identityCode?: string | null }; course?: string | null; scheduleName?: string | null;
	promotionId?: number | null; promoName: string; kind?: string | null; amount: number | null; fits?: string | null; advisors?: { userId?: number | null; name: string; sharePercentage?: number | null }[];
	status: "pending" | "returned" | "applied"; returnedReason?: string | null; returnedAt?: string | null; applicationId?: number | null;
}
export interface PromoRequestList { data: PromoRequestRow[]; total: number; page: number; perPage: number; currencySymbol?: string }

/** Respuesta de POST promotions/_enPersona (docs/CONTRATO_PROMO_EN_CAMINO.md §1). */
export interface EnPersonaCreada { enCamino: EnCamino; message?: string | null }
export interface EnCaminoLista { rows: EnCamino[]; total: number; page: number; perPage: number; canCreate?: boolean }

/** Módulo Promociones: las promos de adelanto ya aplicadas (expediente de cada una) y el buscador de kits del editor. */
export class PromoApplicationsService {
	list = async (campusId: number, f: PromoApplicationFilters): Promise<AxiosResponse<any>> => {
		const params: any = {};
		if (f.from) { params.from = f.from; }
		if (f.to) { params.to = f.to; }
		if (f.statusId) { params.statusId = f.statusId; }
		if (f.promotionId) { params.promotionId = f.promotionId; }
		if (f.advisorId) { params.advisorId = f.advisorId; }
		if (f.q) { params.q = f.q; }
		if (f.tab) { params.tab = f.tab; }
		params.page = f.page || 1;
		if (f.perPage) { params.perPage = f.perPage; }
		return axiosInstance.get<any>(`${base(campusId)}/_applications`, { params });
	};

	/** Resumen de montos del mes para el Tablero de Ventas (scope: campus | country). */
	summary = async (campusId: number, month: string, scope: "campus" | "country"): Promise<AxiosResponse<any>> =>
		axiosInstance.get<any>(`${base(campusId)}/_summary`, { params: { month, scope } });

	detail = async (campusId: number, id: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get<any>(`${base(campusId)}/_applications/${id}`);

	/** Productos activos del catálogo del plantel (kits, uniformes, artículos) para los «Regalos» de una promo. */
	/*
		Solicitud de Promoción de la asesora (docs/ESPEC_SOLICITUD_PROMO.md): un ticket (tb_payment_intents, source_id = 2) con la promo,
		las asesoras y el comprobante ya puestos; Caja lo aplica desde Pagos por Aplicar / Registrar Pago.
	*/
	/** POST promotions/_requests (multipart): crea la solicitud. Responde { intentId, promoName, fits, message }. */
	createRequest = async (campusId: number, form: FormData): Promise<AxiosResponse<PromoRequestCreated>> =>
		axiosInstance.post<PromoRequestCreated>(`${base(campusId)}/_requests`, form, { headers: { "Content-Type": "multipart/form-data" } });

	/** GET promotions/_requests?status=pending|returned|applied&page=: las solicitudes que ve quien pregunta (su equipo o todas). */
	requests = async (campusId: number, params: { status: "pending" | "returned" | "applied"; page?: number; perPage?: number }): Promise<AxiosResponse<PromoRequestList>> =>
		axiosInstance.get<PromoRequestList>(`${base(campusId)}/_requests`, { params: { status: params.status, page: params.page || 1, ...(params.perPage ? { perPage: params.perPage } : {}) } });

	/** POST promotions/_requests/{intentId}/_resend (multipart files[], notes?): comprobante nuevo tras una devolución → ticket nuevo. */
	resendRequest = async (campusId: number, intentId: number, form: FormData): Promise<AxiosResponse<PromoRequestCreated>> =>
		axiosInstance.post<PromoRequestCreated>(`${base(campusId)}/_requests/${intentId}/_resend`, form, { headers: { "Content-Type": "multipart/form-data" } });

	/** POST promotions/_enPersona (JSON, sin comprobante): deja la promo lista para cobrar en Caja (En Camino, Por Cobrar). */
	createEnPersona = async (campusId: number, body: { studentId: number; enrollmentId: number; promotionId: number; giftOptionKey?: string | null; giftEnrollmentId?: number | null; giftStudentId?: number | null; advisors?: { userId: any; sharePercentage?: number | null }[]; notes?: string }): Promise<AxiosResponse<EnPersonaCreada>> =>
		axiosInstance.post<EnPersonaCreada>(`${base(campusId)}/_enPersona`, body);

	/** GET promotions/_enCamino?status=vivas|fallidas|aplicadas|todas&q=&page=. */
	enCaminoList = async (campusId: number, p: { status: "vivas" | "fallidas" | "aplicadas" | "todas"; q?: string; page?: number; perPage?: number }): Promise<AxiosResponse<EnCaminoLista>> =>
		axiosInstance.get<EnCaminoLista>(`${base(campusId)}/_enCamino`, { params: { status: p.status, ...(p.q ? { q: p.q } : {}), page: p.page || 1, ...(p.perPage ? { perPage: p.perPage } : {}) } });

	enCaminoDetail = async (campusId: number, id: number): Promise<AxiosResponse<EnCaminoDetail>> =>
		axiosInstance.get<EnCaminoDetail>(`${base(campusId)}/_enCamino/${id}`);

	/** POST promotions/_enCamino/{id}/_cancel: solo sin pagos. */
	enCaminoCancel = async (campusId: number, id: number, reason: string): Promise<AxiosResponse<EnCamino>> =>
		axiosInstance.post<EnCamino>(`${base(campusId)}/_enCamino/${id}/_cancel`, { reason });

	kitArticles = async (campusId: number, q: string): Promise<AxiosResponse<any>> =>
		axiosInstance.get<any>(`${base(campusId)}/_kitArticles`, { params: { q, kinds: "kit,uniform,article" } });

	/**
	 * «Sugerir Kits por Curso»: los kits ligados a cada curso (rt_kit_courses) visibles en el plantel, por tier_rank.
	 * Con `courseIds` (lo que el editor tiene marcado, aunque no se haya guardado) o con `promotionId` (los cursos de la promo guardada).
	 */
	giftSuggestions = async (campusId: number, p: { promotionId?: number | null; courseIds?: number[] }): Promise<AxiosResponse<any>> =>
		axiosInstance.get<any>(`${base(campusId)}/_giftSuggestions`, { params: {
			...(p.promotionId ? { promotionId: p.promotionId } : {}),
			...(p.courseIds && p.courseIds.length ? { courseIds: p.courseIds.join(",") } : {}) } });
}

export default PromoApplicationsService;
